import { LiveDocShareDialog } from "@app/components/live/LiveDocShareDialog";
import { useLiveHuddle } from "@app/components/live/liveHuddleContext";
import { useTheme } from "@app/components/sparkle/ThemeContext";
import { useSendNotification } from "@app/hooks/useNotification";
import { useAppRouter } from "@app/lib/platform";
import {
  useCommentOnLiveDoc,
  useDownloadLiveDocFile,
  useFetchLiveBrowserVideo,
  useFetchLiveDocToken,
  useFetchLiveMediaToken,
  useFetchLiveTranscribeToken,
  useLeaveLiveCall,
  useLiveBrowserFrames,
  useLiveDocConnection,
  useLiveDocHistory,
  useLiveDock,
  useLiveDocs,
  useLiveDocVersion,
  useLiveRunner,
  usePostLiveDocMention,
  usePostLiveTranscriptLine,
  useRestoreLiveDoc,
  useServerVoice,
} from "@app/lib/swr/live_docs";
import { useUser } from "@app/lib/swr/user";
import { getLiveDocPageRoute } from "@app/lib/utils/router";
import type { LightWorkspaceType } from "@app/types/user";
import { Avatar } from "@dust-tt/sparkle";
import type { LiveHost } from "@memo-air/react";
import { LiveHostProvider } from "@memo-air/react";
import { Notification } from "@memo-air/react/ui";
import type { ReactNode } from "react";
import { useMemo } from "react";

function memoairLiveHost(owner: LightWorkspaceType): LiveHost {
  return {
    useConnection: ({ docId, disabled }) =>
      useLiveDocConnection({ owner, docId, disabled }),
    useFetchToken: (docId) => useFetchLiveDocToken({ owner, docId }),
    useUser: () => {
      const { user } = useUser();
      const id = user?.sId;
      const name = user?.fullName;
      return useMemo(
        () => (id && name !== undefined ? { id, name } : null),
        [id, name]
      );
    },
    useIsDark: () => useTheme().isDark,
    useLiveDocs: (args) => useLiveDocs({ owner, disabled: args?.disabled }),
    // A conversation's call opens as its huddle, over the page; anything else as its own page.
    useOpenDoc: () => {
      const router = useAppRouter();
      const huddle = useLiveHuddle();
      const { liveDocs } = useLiveDocs({ owner });
      return (docId) => {
        const doc = liveDocs.find((candidate) => candidate.sId === docId);
        if (huddle && doc?.kind === "media" && doc.conversationId) {
          void huddle.openHuddle(doc.conversationId, null);
          return;
        }
        void router.push(getLiveDocPageRoute(owner.sId, docId));
      };
    },
    useHistory: ({ docId, disabled }) =>
      useLiveDocHistory({ owner, docId, disabled }),
    useVersion: (args) => useLiveDocVersion({ owner, ...args }),
    useRestore: (docId) => useRestoreLiveDoc({ owner, docId }),
    useComment: (docId) => useCommentOnLiveDoc({ owner, docId }),
    usePostMention: (docId) => usePostLiveDocMention({ owner, docId }),
    useDownloadFile: (args) => useDownloadLiveDocFile({ owner, ...args }),
    useBrowserFrames: (args) => useLiveBrowserFrames({ owner, ...args }),
    useFetchBrowserVideo: (docId) => useFetchLiveBrowserVideo({ owner, docId }),
    useFetchMediaToken: (args) => useFetchLiveMediaToken({ owner, ...args }),
    usePostTranscriptLine: (docId) =>
      usePostLiveTranscriptLine({ owner, docId }),
    useFetchTranscribeToken: () => useFetchLiveTranscribeToken({ owner }),
    useServerVoice: () => useServerVoice({ owner }),
    useLeaveCall: (docId) => useLeaveLiveCall({ owner, docId }),
    useRunner: (docId, runKey) => useLiveRunner({ owner, docId, runKey }),
    useDock: (space) => {
      const huddle = useLiveHuddle();
      return useLiveDock({ owner, space, onCall: huddle?.openHuddle ?? null });
    },
    // Memoair's live-voice hears people through their browsers' transcripts, and calls open as a
    // huddle over the page rather than as a window on the desktop.
    serverVoiceHearsText: true,
    callsOutsideSpace: true,
    // The chat's own avatars: seeded illustrations unless a real picture exists, people round.
    renderAvatar: (member, size) => (
      <Avatar
        name={member.name}
        visual={member.pictureUrl ?? undefined}
        isRounded={member.kind === "human"}
        size={size}
      />
    ),
    renderShare: (args) => <LiveDocShareDialog owner={owner} {...args} />,
  };
}

interface MemoairLiveHostProviderProps {
  owner: LightWorkspaceType;
  children: ReactNode;
}

// The live views come from @memo-air/react; this is Memoair's data behind them.
export function MemoairLiveHostProvider({
  owner,
  children,
}: MemoairLiveHostProviderProps) {
  const host = useMemo(() => memoairLiveHost(owner), [owner]);
  // The views' toasts go through Memoair's own, logged like every other UI error.
  const sendNotification = useSendNotification();
  return (
    <LiveHostProvider host={host}>
      <Notification.Area notify={sendNotification}>
        {children}
      </Notification.Area>
    </LiveHostProvider>
  );
}
